/**
 * Facet dropdown options for the Contact Groups list, extracted from the page
 * so the option lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::ContactListsController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `CONTACT_GROUPS_TABLE.facetKeys`,
 * and by
 * `xop-brain/apps/crm-api/test/controllers/concerns/declared_keys_export_test.rb`
 * on the server side.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { CONTACT_GROUPS_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { ContactListFilters } from "@crm-src/lib/contact-lists-api";

/**
 * The Contact Groups facet keys, DERIVED from the shared config rather than
 * re-typed — so there is exactly one place a key can be misspelled, and
 * misspelling it there breaks the assertion below.
 */
export type ContactGroupFacetKey = FacetKeysOf<typeof CONTACT_GROUPS_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param
 * `ContactListFilters` actually carries — i.e. one the API implements. A key
 * that only exists in the config otherwise compiles fine and fails at RUNTIME
 * as a param the server drops, with `config-parity.test.ts` as the only net.
 */
type FacetKeysAreFilterFields =
  ContactGroupFacetKey extends keyof ContactListFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

/**
 * Build the Contact Groups filter-bar facets.
 *
 * Options come from data the page ALREADY has — the user roster — so no
 * endpoint exists just to populate a dropdown.
 *
 * The single facet is the group's OWNER (`contact_lists.owner_id`, which the
 * table's fourth column renders as `owner.full_name`). It bites for an actor
 * that sees all records — the index lists every group to them (crm-api
 * `visible_contact_lists`, 2026-10-01). For an own-scope actor the index is
 * already that user's own groups, and narrowing by owner can only return the
 * same rows or none. Either way it is a NARROWING, never a widening, which is
 * why the facet is safe to offer unconditionally.
 *
 * Returns the facet even when the roster came back empty (`useUsers()` answers
 * 403 with `[]` for a non-admin): dropping an optionless facet from the
 * rendered controls is `TableFilterBar`'s job, so every list page inherits it.
 * This stays a pure function of its inputs.
 */
export function buildContactGroupFacetDefs(
  users: PersonLike[],
): FacetDef<ContactGroupFacetKey>[] {
  return [
    {
      key: "owner_id",
      label: "Owner",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
  ];
}
